import React, { useState, useEffect } from 'react'; // Importer useEffect
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext'; // Importer useAuth
import api from '../../services/api';
import { useNavigate } from 'react-router-dom'; // Importer useNavigate
import { useTranslation } from 'react-i18next'; // Ajouter l'import pour la traduction
import WelcomeConfettiEffect from './WelcomeConfettiEffect';
import WelcomeGuideDialog from './WelcomeGuideDialog';

// --- Configuration de la vidéo de bienvenue ---
// Format : '16:9' (paysage) ou '9:16' (vertical / Reel)
const VIDEO_ASPECT_RATIO = '9:16';

const getAspectPadding = (ratio) => {
  const [w, h] = ratio.split(':').map(Number);
  return `${(h / w) * 100}%`;
};

// REMPLACEZ par l'URL embed de votre vidéo
const WELCOME_VIDEO_URL = 'https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1941510943215055&show_text=false';

const logger = {
  info: (message, ...args) => console.log(`%c[EmailVerification] [INFO] ${message}`, 'color: purple;', ...args),
  debug: (message, ...args) => console.log(`%c[EmailVerification] [DEBUG] ${message}`, 'color: gray;', ...args),
  warn: (message, ...args) => console.warn(`%c[EmailVerification] [WARN] ${message}`, 'color: orange;', ...args),
  error: (message, ...args) => console.error(`%c[EmailVerification] [ERROR] ${message}`, 'color: red;', ...args),
};

const EmailVerification = ({ email, autoLoginOnSuccess = true, onVerificationSuccess = null }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(''); // Renommé pour clarté
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  // Ajouter le hook useTranslation
  const { t } = useTranslation();

  // Obtenir la fonction du contexte
  const { updateAuthState, updateUserPreferences } = useAuth();
  const navigate = useNavigate(); // Hook pour la navigation

  useEffect(() => {
    logger.info(`EmailVerification component mounted for email: ${email}`);
    if (!email) {
        logger.error("Email prop is missing!");
        setError(t('auth.verification.errors.emailMissing', 'Une erreur interne est survenue (email manquant).'));
    }
  }, [email, t]);


  const handleVerify = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) { // Validation simple
      setError(t('auth.verification.errors.invalidCode', 'Veuillez entrer le code de vérification (6 chiffres).'));
      return;
    }

    logger.info(`Attempting to verify email ${email} with code ${verificationCode}`);
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Appel API avec le payload CORRIGÉ
      const response = await api.post('/auth/verify-email', {
        email,
        verificationCode: verificationCode // Clé corrigée
      });
      logger.info('Verification API call successful:', response.data);

      // --- NOUVELLE LOGIQUE DE GESTION ---
      // Attendre user et token de la réponse (nécessite modif backend)
      const { user: verifiedUser, token: newToken, message } = response.data;

      if (verifiedUser && newToken) {
          const successText = message || t('auth.verification.success', 'Email vérifié avec succès ! Connexion en cours...');
          setSuccessMessage(successText);

          if (!autoLoginOnSuccess && typeof onVerificationSuccess === 'function') {
            logger.info('Verification successful, delegating next step to parent flow.');
            await onVerificationSuccess({
              email,
              user: verifiedUser,
              token: newToken,
              message: successText
            });
            return;
          }

          logger.info('Verification successful, received user and token. Calling updateAuthState...');

          // Appeler la fonction du contexte pour mettre à jour l'état global
          await updateAuthState(verifiedUser, newToken);

          logger.info('Auth state updated by context.');
          const hasSeenWelcome = verifiedUser?.preferences?.hasSeenWelcomeVideo;
          if (!hasSeenWelcome) {
            setShowWelcomeDialog(true);
          } else {
            navigate('/dashboard');
          }
          // --- FIN NOUVELLE LOGIQUE ---

      } else {
          // Si le backend ne renvoie pas user/token (état actuel ou erreur)
          logger.error('Verification API response did not contain expected user/token data.');
          setError(t('auth.verification.errors.loginFailed', 'La vérification a réussi mais la connexion a échoué. Veuillez vous connecter manuellement.'));
          // Optionnel: rediriger vers login après un délai
          setTimeout(() => navigate('/login'), 3000);
      }

    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.msg || t('auth.verification.errors.failed', 'La vérification a échoué.');
      logger.error(`Verification failed: ${message}`, err.response || err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    logger.info(`Requesting resend verification code for ${email}`);
    setResendLoading(true); // Utiliser l'état de chargement dédié
    setError('');
    setSuccessMessage('');
    setResendSuccess(false); // Réinitialiser avant l'appel

    try {
      await api.post('/auth/resend-verification', { email });
        logger.info('Resend code request successful.');
        setResendSuccess(true); // Afficher le message de succès pour le renvoi

        // Réinitialiser le message après quelques secondes
        setTimeout(() => {
            setResendSuccess(false);
        }, 5000);
    } catch (err) {
        const message = err.response?.data?.message || err.response?.data?.msg || t('auth.verification.errors.resendFailed', 'Impossible de renvoyer le code.');
        logger.error(`Failed to resend code: ${message}`, err.response || err);
        setError(message); // Afficher l'erreur
    } finally {
        setResendLoading(false);
    }
  };

  const handleContinueToDashboard = async () => {
    try {
      await updateUserPreferences({ hasSeenWelcomeVideo: true });
    } catch (err) {
      logger.error('Failed to update welcome preference:', err);
    }
    setShowWelcomeDialog(false);
    navigate('/dashboard');
  };

  const handleSkipWelcome = async () => {
    try {
      await updateUserPreferences({ hasSeenWelcomeVideo: true });
    } catch (err) {
      logger.error('Failed to update welcome preference:', err);
    }
    setShowWelcomeDialog(false);
    navigate('/dashboard');
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: { xs: 2, sm: 'auto' }, mt: 4, mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
        {t('auth.verification.title', 'Vérification de votre adresse email')}
      </Typography>

      <Typography variant="body1" paragraph align="center">
        {t('auth.verification.instructions', 'Un code de vérification a été envoyé à')} <strong>{email}</strong>.
        {t('auth.verification.enterCodeBelow', 'Veuillez entrer ce code ci-dessous pour vérifier votre adresse email.')}
      </Typography>

      <Typography
        variant="body2"
        align="center"
        sx={{
          color: 'error.main',
          backgroundColor: 'white',
          py: 1.5,
          px: 2,
          borderRadius: 1,
          mb: 2,
          fontWeight: 500,
          border: '1px solid',
          borderColor: 'error.main',
        }}
      >
        {t('auth.verification.spamWarning', "N'oubliez pas de vérifier vos spams / courriers indésirables.")}
      </Typography>

      {error && ( <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert> )}
      {/* Afficher le message de succès général ou celui du renvoi */}
      {successMessage && !error && ( <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{successMessage}</Alert> )}
      {resendSuccess && !error && !successMessage && ( <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{t('auth.verification.codeSent', 'Un nouveau code a été envoyé.')}</Alert> )}


      <Box component="form" onSubmit={handleVerify} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="verificationCode"
          label={t('auth.verification.codeLabel', 'Code de vérification')}
          name="verificationCode" // Name correspond à l'id
          autoComplete="off"
          autoFocus
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
          sx={{ mb: 2 }}
          disabled={loading || !!successMessage} // Désactiver si succès ou chargement
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading || !!successMessage || !verificationCode} // Désactiver si succès, chargement ou code vide
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : t('auth.verification.verify', 'Vérifier')}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="text"
            color="primary"
            onClick={handleResendCode}
            disabled={loading || resendLoading || !!successMessage} // Désactiver pendant tous les chargements ou si succès
          >
            {resendLoading ? <CircularProgress size={24} /> : t('auth.verification.resendCode', 'Renvoyer le code')}
          </Button>
        </Box>
      </Box>
      </Paper>

      {showWelcomeDialog && (
        <WelcomeConfettiEffect
          welcomeTitle={t('welcome.title', 'Bienvenue sur Krillou !')}
          welcomeSubtitle={t('welcome.subtitle', 'Découvrez en quelques minutes comment créer votre premier listing ou produit.')}
        />
      )}

      <WelcomeGuideDialog
        open={showWelcomeDialog}
        onClose={handleContinueToDashboard}
      />
    </>
  );
};

export default EmailVerification;